import React, { useState } from 'react';
import { api, clearSessionToken } from '../lib/api';
import { useAuth } from '../lib/auth';
import { motion } from 'framer-motion';
import { KeyRound, Loader2, Check } from 'lucide-react';

export default function ChangePassword() {
  const { user, refresh } = useAuth();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (next.length < 8) { setError('New password must be at least 8 characters'); return; }
    if (next !== confirm) { setError('Passwords do not match'); return; }
    setBusy(true);
    try {
      await api.post('/auth/change-password', { current_password: current, new_password: next });
      setDone(true);
      if (refresh) await refresh();
      setTimeout(() => { window.location.href = '/app/dashboard'; }, 1200);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Failed to change password');
    } finally { setBusy(false); }
  };

  const logout = async () => {
    try { await api.post('/auth/logout'); } catch { /* ignore */ }
    clearSessionToken();
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen grid place-items-center p-6" data-testid="change-password-page">
      <div className="ambient-bg" />
      <motion.div
        initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="glass-strong w-full max-w-md p-10"
      >
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-[#1A2530] to-[#282828] grid place-items-center">
            <KeyRound className="text-white" size={22}/>
          </div>
          <div>
            <div className="overline">Security</div>
            <h1 className="font-serif text-2xl">
              {user?.must_change_password ? 'Set your password' : 'Change password'}
            </h1>
          </div>
        </div>

        {user?.must_change_password && !done && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 mb-6 text-xs text-amber-900">
            Welcome, <b>{user.name}</b>. This is your first login — please choose a personal password to continue.
          </div>
        )}

        {done ? (
          <div className="text-center py-6" data-testid="password-changed-ok">
            <div className="w-14 h-14 mx-auto rounded-full bg-emerald-100 grid place-items-center mb-3">
              <Check className="text-emerald-600" size={28}/>
            </div>
            <div className="font-serif text-xl">Password updated</div>
            <div className="text-sm text-slate-500 mt-1">Redirecting to your dashboard…</div>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">
                {user?.must_change_password ? 'Temporary password (from admin)' : 'Current password'}
              </label>
              <input type="password" value={current} onChange={(e) => setCurrent(e.target.value)}
                required autoComplete="current-password" data-testid="cp-current-input"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">New password</label>
              <input type="password" value={next} onChange={(e) => setNext(e.target.value)}
                required minLength={8} autoComplete="new-password" data-testid="cp-new-input"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
              <div className="text-[11px] text-slate-500 mt-1">Minimum 8 characters.</div>
            </div>
            <div>
              <label className="text-xs font-medium text-slate-600 mb-1.5 block">Confirm new password</label>
              <input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)}
                required minLength={8} autoComplete="new-password" data-testid="cp-confirm-input"
                className="w-full px-4 py-2.5 rounded-lg border border-slate-200 bg-white/70 text-sm focus:outline-none focus:ring-2 focus:ring-[#679286]/40"/>
            </div>
            {error && <div data-testid="cp-error" className="text-xs text-red-600 bg-red-50 border border-red-100 rounded-md px-3 py-2">{error}</div>}
            <button type="submit" disabled={busy} data-testid="cp-submit-btn"
              className="w-full py-3 rounded-full bg-slate-900 text-white text-sm flex items-center justify-center gap-2 hover:bg-slate-800 disabled:opacity-60">
              {busy && <Loader2 size={14} className="animate-spin"/>}
              Update password
            </button>
            {user?.must_change_password ? (
              <button type="button" onClick={logout} className="w-full py-2 text-xs text-slate-500 hover:text-slate-700">
                Sign out instead
              </button>
            ) : (
              <a href="/app/dashboard" className="block w-full py-2 text-center text-xs text-slate-500 hover:text-slate-700">
                Cancel and go back
              </a>
            )}
          </form>
        )}
      </motion.div>
    </div>
  );
}
